/**
 * Copy and paste.
 *
 * What is copied is a small, self-contained piece of design: the selected
 * components, the lines running between them, and any whole block instances
 * selected. Pasting gives every copied element a fresh identifier (invariant
 * 2: a paste is new elements, never a second use of a name) and rewrites the
 * references between them, so the pasted piece is wired exactly as the
 * original was.
 *
 * Lines to anything outside the copy are left behind: a port carries one line,
 * and the original still holds it. Region applications (treatments, deposits,
 * zones) are not copied; they are applied to a region deliberately, and a
 * copy silently inheriting one would put a reagent somewhere nobody chose.
 *
 * The clipboard lives in editor state rather than the system clipboard, so it
 * holds design documents and nothing else, and needs no parsing on the way
 * back in.
 */
import type { DesignDocument, EdgeDocument, GroupInstanceDocument, NodeDocument, TextNoteDocument } from "../design/document";
import type { Point } from "../scene/types";
import { allocateIdentifier, normaliseRole } from "./identifiers";

export interface ClipboardContent {
  nodes: NodeDocument[];
  edges: EdgeDocument[];
  instances: GroupInstanceDocument[];
  /** Selected notes. One keeps its component only if that component came too. */
  notes: TextNoteDocument[];
  /** How many times this content has been pasted, so repeated pastes cascade. */
  pastes: number;
  /** Lines touching the copy that could not come with it: one end was outside. */
  leftBehind: number;
}

const top = (ref: string) => ref.split(".")[0]!;

/**
 * The part of the design a copy takes, or null when the selection holds
 * nothing copyable. A selected block instance brings its members; a locked
 * member selected on its own is copied as a free-standing component.
 */
export function copySelection(design: DesignDocument, selection: ReadonlySet<string>): ClipboardContent | null {
  const instances = (design.group_instances ?? []).filter(
    (instance) => !instance.detached && selection.has(instance.local_id),
  );
  const wholeBlocks = new Map(
    instances.flatMap((instance) => (instance.member_local_ids ?? []).map((member) => [member, instance.local_id] as const)),
  );
  const nodes = (design.nodes ?? [])
    .filter((node) => selection.has(node.local_id) || wholeBlocks.has(node.local_id))
    .map((node) => {
      // Membership comes with the block, or not at all.
      const instance = wholeBlocks.get(node.local_id);
      const { group_instance: _membership, ...rest } = node;
      void _membership;
      return instance ? { ...rest, group_instance: instance } : rest;
    });
  const copied = new Set(nodes.map((node) => node.local_id));
  const notes = (design.text_notes ?? [])
    .filter((note) => selection.has(note.local_id))
    .map((note) => {
      if (!note.anchor || copied.has(note.anchor)) return note;
      const { anchor: _anchor, ...rest } = note;
      void _anchor;
      return rest;
    });
  if (nodes.length === 0 && notes.length === 0) return null;
  const edges = (design.edges ?? []).filter(
    (edge) => copied.has(top(edge.from_port)) && copied.has(top(edge.to_port)),
  );
  const leftBehind = (design.edges ?? []).filter(
    (edge) => copied.has(top(edge.from_port)) !== copied.has(top(edge.to_port)),
  ).length;
  return { nodes, edges, instances, notes, pastes: 0, leftBehind };
}

function stemOf(localId: string): string {
  // valve_3 -> valve; inlet_valve -> inlet_valve.
  const match = /^(.*)_\d+$/.exec(localId);
  return normaliseRole(match ? match[1]! : localId);
}

/**
 * Paste `content` into `design`, moved by `offset`. Returns the new document
 * and the references of what was pasted, for selecting it.
 */
export function pasteContent(
  design: DesignDocument,
  content: ClipboardContent,
  offset: Point,
  used: Set<string>,
): { document: DesignDocument; refs: string[] } {
  const renamed = new Map<string, string>();
  const fresh = (localId: string) => {
    const next = allocateIdentifier(stemOf(localId), used);
    renamed.set(localId, next);
    return next;
  };
  // Nodes first, then edges and instances, so numbering reads in that order.
  for (const node of content.nodes) fresh(node.local_id);
  for (const edge of content.edges) fresh(edge.local_id);
  for (const instance of content.instances) fresh(instance.local_id);
  for (const note of content.notes ?? []) fresh(note.local_id);

  const port = (ref: string) => {
    const [owner, ...rest] = ref.split(".");
    return [renamed.get(owner!) ?? owner, ...rest].join(".");
  };
  const moved = (point: Point) => ({ x: point.x + offset.x, y: point.y + offset.y });

  const nodes: NodeDocument[] = content.nodes.map((node) => ({
    ...node,
    local_id: renamed.get(node.local_id)!,
    ...(node.group_instance ? { group_instance: renamed.get(node.group_instance) ?? null } : {}),
    ...(node.schematic_position
      ? { schematic_position: { ...node.schematic_position, ...moved(node.schematic_position) } }
      : {}),
    // Phase 2's physical placement is not copied: two components cannot
    // occupy one place on the part, and the copy has not been laid out.
    placement: null,
    // Mounted on a tube that came too: on the copy of it. Otherwise the copy
    // stands where the part was drawn, on no tube (D40).
    mount: node.mount && renamed.has(node.mount.edge) ? { ...node.mount, edge: renamed.get(node.mount.edge)! } : null,
  }));
  const edges: EdgeDocument[] = content.edges.map((edge) => ({
    ...edge,
    local_id: renamed.get(edge.local_id)!,
    from_port: port(edge.from_port),
    to_port: port(edge.to_port),
    ...(edge.schematic_waypoints ? { schematic_waypoints: edge.schematic_waypoints.map(moved) } : {}),
    // Not laid out either (Phase 2 seam).
    layer_assignment: null,
  }));
  const instances: GroupInstanceDocument[] = content.instances.map((instance) => ({
    ...instance,
    local_id: renamed.get(instance.local_id)!,
    member_local_ids: (instance.member_local_ids ?? []).map((member) => renamed.get(member) ?? member),
    member_origins: Object.fromEntries(
      Object.entries(instance.member_origins ?? {}).map(([member, origin]) => [renamed.get(member) ?? member, origin]),
    ),
  }));

  const notes: TextNoteDocument[] = (content.notes ?? []).map((note) => ({
    ...note,
    local_id: renamed.get(note.local_id)!,
    position: moved(note.position),
    ...(note.anchor ? { anchor: renamed.get(note.anchor) ?? note.anchor } : {}),
  }));

  return {
    document: {
      ...design,
      ...(notes.length ? { text_notes: [...(design.text_notes ?? []), ...notes] } : {}),
      nodes: [...(design.nodes ?? []), ...nodes],
      edges: [...(design.edges ?? []), ...edges],
      ...(instances.length ? { group_instances: [...(design.group_instances ?? []), ...instances] } : {}),
    },
    refs: [
      // A pasted block is selected as the block, not as its members.
      ...nodes.filter((node) => !node.group_instance).map((node) => node.local_id),
      ...edges.filter((edge) => !instances.some((i) => i.member_local_ids?.includes(edge.local_id))).map((e) => e.local_id),
      ...instances.map((instance) => instance.local_id),
      ...notes.map((note) => note.local_id),
    ],
  };
}
